import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { useTheme } from '../../../contexts/ThemeContext'
import { useLatest } from '../useLatest'
import { snapshotSvg } from '../../../utils/snapshotSvg'
import ExportButtons from '../../../components/ExportButtons'
import CellTooltip from '../../../components/CellTooltip'
import type { ColumnDefinition } from '../../../utils/exportToCsv'
import { fetchPeriodeSerie } from './api'
import type { Granularity, PeriodeBucket, PeriodeSerieResponse, PeriodeSeries } from './api'
import { comparisonRange } from './periodeComparison'
import type { ComparisonMode } from './periodeComparison'

/**
 * Overdragelser per dag, uke or måned for the selected period, with the
 * comparison period dashed in the same colour (#928 AC3), after Perioder →
 * Graf. Buckets are aligned by index. In omregistrering mode the backend
 * buckets on the omregistrering date, not the sale (#1033).
 */

interface Props {
  from: string
  to: string
  klasse: string[]
  varebil: boolean | undefined
  omregistrering: boolean
  selgerPostnummer: string | null
  kjoperPostnummer: string | null
  // Shared with the top lists, so both compare with the same period.
  mode: ComparisonMode
  onModeChange: (mode: ComparisonMode) => void
}

const SERIES_COLORS = ['#0891b2', '#e11d48', '#7c3aed', '#059669', '#d97706']
const TOTAL = '__total__'

const fmtInt = (n: number) => n.toLocaleString('nb-NO')
const valueOf = (b: PeriodeBucket, key: string) => (key === TOTAL ? b.total : b.klasser[key] ?? 0)

export default function PeriodeGraf({ from, to, klasse, varebil, omregistrering, selgerPostnummer, kjoperPostnummer, mode, onModeChange }: Props) {
  const { t } = useTranslation('bruktbil')
  const { isDark } = useTheme()
  const [granularity, setGranularity] = useState<Granularity>('week')
  const [showComparison, setShowComparison] = useState(true)
  const [data, setData] = useState<PeriodeSerieResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const chartImage = useRef<string | undefined>(undefined)

  const prev = useMemo(() => comparisonRange(from, to, mode, granularity), [from, to, mode, granularity])
  const klasseKey = klasse.join('|')

  useLatest(alive => {
    setLoading(true)
    setError(null)
    fetchPeriodeSerie(
      { from, to, klasse: klasseKey ? klasseKey.split('|') : [], varebil, omregistrering, selgerPostnummer, kjoperPostnummer },
      granularity,
      prev,
    )
      .then(r => { if (alive()) setData(r.data) })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : '') })
      .finally(() => { if (alive()) setLoading(false) })
  }, [from, to, klasseKey, varebil, omregistrering, selgerPostnummer, kjoperPostnummer, granularity, prev.from, prev.to])

  // One line per selected klasse, or one total line.
  const lines = useMemo(() => (data?.klasser.length ? data.klasser : [TOTAL]), [data])
  const lineName = (key: string) => (key === TOTAL ? t('oversikt.graf.overdragelser') : key)
  const hasComparison = showComparison && !!data?.comparison

  const chartData = useMemo(() => {
    if (!data) return []
    const prevBuckets = data.comparison?.buckets ?? []
    return data.current.buckets.map((c, i) => {
      const p = prevBuckets[i]
      // Buckets past the data cut-off would draw as a collapse to zero.
      const noData = c.period_start > data.data_through
      const row: Record<string, string | number | boolean | null> = {
        index: i,
        label: c.label,
        curLabel: c.label,
        curPartial: c.is_partial,
        prevLabel: p ? p.label : null,
        prevPartial: !!p?.is_partial,
        prevBeforeHistory: !!p?.before_history,
      }
      for (const key of lines) {
        row[`c_${key}`] = noData ? null : valueOf(c, key)
        // A comparison bucket before the data history is unknown, not zero.
        row[`p_${key}`] = p && !p.before_history ? valueOf(p, key) : null
      }
      return row
    })
  }, [data, lines])

  useEffect(() => {
    chartImage.current = undefined
    if (!chartData.length) return
    const timer = setTimeout(() => snapshotSvg(chartRef.current, png => { chartImage.current = png }), 600)
    return () => clearTimeout(timer)
  }, [chartData, hasComparison, isDark])

  const exportRows = useMemo(() => {
    if (!data) return []
    const rows: Record<string, string | number>[] = []
    const add = (periode: string, s: PeriodeSeries) => {
      for (const b of s.buckets) {
        for (const key of lines) {
          rows.push({
            periode,
            label: b.label,
            fra: b.period_start,
            til: b.period_end,
            serie: key === TOTAL ? t('oversikt.graf.overdragelser') : key,
            antall: b.before_history ? '' : valueOf(b, key),
            delvis: b.is_partial ? t('oversikt.graf.ja') : '',
          })
        }
      }
    }
    add(t('oversikt.graf.valgtPeriode'), data.current)
    if (data.comparison) add(t('oversikt.graf.sammenligning'), data.comparison)
    return rows
  }, [data, lines, t])

  const exportColumns: ColumnDefinition[] = [
    { key: 'periode', label: t('oversikt.graf.periode') },
    { key: 'label', label: t(`oversikt.graf.granularitet.${granularity}`) },
    { key: 'fra', label: t('oversikt.graf.fra') },
    { key: 'til', label: t('oversikt.graf.til') },
    { key: 'serie', label: t('oversikt.graf.serie') },
    { key: 'antall', label: t('common.antall') },
    { key: 'delvis', label: t('oversikt.graf.delvisPeriode') },
  ]

  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  // Hollow marker on a partially covered current bucket so its short value is
  // not read as a drop; a solid one on a point with no neighbour (#782/#861).
  const pointDot = (stroke: string, dataKey: string, markPartial: boolean) =>
    (props: { key?: string; cx?: number; cy?: number; index?: number; value?: number | null; payload?: Record<string, unknown> }) => {
      if (props.value == null || props.cx == null || props.cy == null) return <g key={props.key} />
      const i = props.index ?? 0
      if (markPartial && props.payload?.curPartial) {
        return (
          <circle key={props.key} cx={props.cx} cy={props.cy} r={4} fill={isDark ? '#1e293b' : '#ffffff'}
            stroke={stroke} strokeWidth={2} data-testid="periode-partial-dot" />
        )
      }
      if (chartData[i - 1]?.[dataKey] == null && chartData[i + 1]?.[dataKey] == null) {
        return <circle key={props.key} cx={props.cx} cy={props.cy} r={4} fill={stroke} />
      }
      return <g key={props.key} />
    }

  const renderTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) return null
    const row = payload[0].payload as Record<string, unknown>
    const block = (prefix: 'c' | 'p', label: unknown, title: string, flags: string[]) =>
      label ? (
        <div className="mt-1">
          <div className="font-semibold">
            {title}: {String(label)}
            {flags.map(f => <span key={f} className="ml-1 text-amber-600 dark:text-amber-400">{f}</span>)}
          </div>
          {lines.map((key, i) => (
            <div key={key} className="flex justify-between gap-4">
              <span style={{ color: SERIES_COLORS[i % SERIES_COLORS.length] }}>{lineName(key)}</span>
              <span>{row[`${prefix}_${key}`] == null ? '–' : fmtInt(Number(row[`${prefix}_${key}`]))}</span>
            </div>
          ))}
        </div>
      ) : null
    return (
      <div className="rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-2 text-xs shadow-sm">
        {block('c', row.curLabel, t('oversikt.graf.valgtPeriode'), [
          ...(row.curPartial ? [t('oversikt.graf.delvisPeriode')] : []),
        ])}
        {hasComparison && block('p', row.prevLabel, t('oversikt.graf.sammenligning'), [
          ...(row.prevPartial ? [t('oversikt.graf.delvisPeriode')] : []),
          ...(row.prevBeforeHistory ? [t('oversikt.graf.forHistorikk')] : []),
        ])}
      </div>
    )
  }

  // tip: a hover tooltip per option; the caller may instead wrap the group.
  const segmented = <T extends string>(options: readonly T[], value: T, onChange: (v: T) => void, label: (v: T) => string, aria: string, tip?: (v: T) => string) => (
    <div className="inline-flex rounded-sm border border-gray-200 dark:border-dark-border overflow-hidden" role="group" aria-label={aria}>
      {options.map(o => {
        const button = (
          <button key={o} type="button" aria-pressed={value === o} onClick={() => onChange(o)}
            className={`px-3 py-1 ${value === o ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}>
            {label(o)}
          </button>
        )
        return tip ? <CellTooltip key={o} content={tip(o)} width={320}>{button}</CellTooltip> : button
      })}
    </div>
  )

  return (
    <div className="card p-4 space-y-4" data-testid="periode-graf">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('oversikt.graf.tittel')}</h3>
        <ExportButtons
          data={exportRows}
          columns={exportColumns}
          metadata={{
            title: t('oversikt.graf.tittel'),
            dateRange: t('oversikt.graf.exportDateRange', { from, to, prevFrom: prev.from, prevTo: prev.to }),
            filters: [
              omregistrering ? t('filterSummary.kunOmregistrering') : t('filterSummary.alleEierskifter'),
              klasse.length ? klasse.join(', ') : t('filterSummary.alleKlasser'),
            ],
          }}
          disabled={loading}
          onGetChartImage={() => chartImage.current}
        />
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <CellTooltip content={t('oversikt.graf.granularitetTooltip')} width={320}>
          {segmented(['day', 'week', 'month'] as const, granularity, setGranularity,
            g => t(`oversikt.graf.granularitet.${g}`), t('oversikt.graf.granularitetAria'))}
        </CellTooltip>
        {segmented(['i_fjor', 'forrige_periode'] as const, mode, onModeChange,
          m => t(`oversikt.graf.mode.${m}`), t('oversikt.graf.modeAria'),
          m => t(`oversikt.graf.modeTooltip.${m}`))}
        <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={showComparison} onChange={e => setShowComparison(e.target.checked)} />
          {t('oversikt.graf.visSammenligning')}
        </label>
      </div>

      {error !== null ? (
        <p className="text-sm text-red-600 dark:text-red-400 py-12 text-center">
          {t('oversikt.feilVedLasting', { error: error || t('oversikt.ukjentFeil') })}
        </p>
      ) : loading && !data ? (
        <div data-testid="chart-skeleton" className="animate-pulse h-80 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : (
        <div ref={chartRef} className={loading ? 'opacity-50' : ''}>
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="label" tick={{ fill: axisColor, fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
              <YAxis tick={{ fill: axisColor, fontSize: 11 }} tickFormatter={v => Number(v).toLocaleString('nb-NO')} />
              <Tooltip content={renderTooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {lines.map((key, i) => (
                <Line key={`c_${key}`} type="monotone" dataKey={`c_${key}`} name={lineName(key)}
                  stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2}
                  dot={pointDot(SERIES_COLORS[i % SERIES_COLORS.length], `c_${key}`, true)}
                  isAnimationActive={false} connectNulls={false} />
              ))}
              {hasComparison && lines.map((key, i) => (
                <Line key={`p_${key}`} type="monotone" dataKey={`p_${key}`}
                  name={t('oversikt.graf.sammenligningSerie', { serie: lineName(key) })}
                  stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={1.5} strokeDasharray="5 5"
                  dot={pointDot(SERIES_COLORS[i % SERIES_COLORS.length], `p_${key}`, false)}
                  isAnimationActive={false} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {data && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="periode-method">
          {t('oversikt.graf.metode', { date: data.data_through.split('-').reverse().join('.') })}
        </p>
      )}
    </div>
  )
}
